import React, { useState, useMemo } from "react";

/**
 * Smart YouTube thumbnail with fallback chain.
 * Tries maxresdefault → sddefault → hqdefault → custom fallback.
 */
function ytId(url) {
    if (!url) return null;
    const m = url.match(/(?:v=|youtu\.be\/|embed\/)([A-Za-z0-9_-]{11})/);
    return m ? m[1] : null;
}

const YT_QUALITIES = ["maxresdefault", "sddefault", "hqdefault", "mqdefault"];

export default function SmartImage({ src, videoUrl, alt, className, fallback, ...props }) {
    const id = useMemo(() => ytId(videoUrl) || ytId(src), [videoUrl, src]);
    const isYouTube = !!id;

    const [qIdx, setQIdx] = useState(0);
    const [failed, setFailed] = useState(false);

    const currentSrc = isYouTube
        ? `https://img.youtube.com/vi/${id}/${YT_QUALITIES[qIdx]}.jpg`
        : src;

    const handleError = () => {
        if (isYouTube && qIdx < YT_QUALITIES.length - 1) {
            setQIdx(qIdx + 1);
        } else {
            setFailed(true);
        }
    };

    // YouTube returns a 120x90 grey placeholder when video doesn't have the requested quality.
    // We detect this by loading the image and checking its natural dimensions.
    const handleLoad = (e) => {
        if (isYouTube && qIdx < YT_QUALITIES.length - 1) {
            // The grey "no thumbnail" image is exactly 120x90
            if (e.target.naturalWidth <= 120) {
                setQIdx(qIdx + 1);
            }
        }
    };

    if (failed && fallback) {
        return (
            <div className={`${className} flex items-center justify-center bg-[var(--bg-elev-2)]`}>
                <span className="text-[var(--ink-soft)] italic-serif text-sm px-4 text-center">{fallback}</span>
            </div>
        );
    }

    return (
        <img
            src={currentSrc}
            alt={alt}
            className={className}
            onError={handleError}
            onLoad={handleLoad}
            loading="lazy"
            {...props}
        />
    );
}
